/**
 * Container for a shared player search result row
 *
 * Derives display values (name, position, club) from a PlayerSearchPlayerFragment
 * and resolves the metric badge via getMetricValue. Accepts an action ReactNode
 * slot for context-specific actions (Add button, chevron arrow, etc.).
 * @module components/shared/search-modal/PlayerSearchResultRow/PlayerSearchResultRowContainer
 */

import React, { memo, useCallback, useMemo, type ReactNode } from "react";

import PlayerSearchResultRowView, {
  type SearchResultEntityType,
} from "./PlayerSearchResultRowView";

import getMetricValue from "../utils/getMetricValue";

import { type PlayerSearchPlayerFragment } from "@/generated/graphql";
import { SORT_FIELD_LABELS } from "@/features/scouting/constants/sortOptions";

/**
 * Props for PlayerSearchResultRowContainer
 */
interface PlayerSearchResultRowContainerProps {
  /** Player data fragment */
  readonly player: PlayerSearchPlayerFragment;
  /** Currently active sort field for metric badge */
  readonly effectiveSortBy: string;
  /** Display label for the sort field (e.g. "GPR", "Style Fit") */
  readonly sortFieldLabel: string;
  /** Action element rendered on the right side of the row */
  readonly action: ReactNode;
  /** Optional press handler for the row (e.g. navigate to player) */
  readonly onPress?: () => void;
  /**
   * Entity type the row represents. Defaults to "Player"; scaffolded for
   * future Report / Note results (SE-5018). When omitted, all existing
   * call sites render unchanged (Player rows show no leading badge).
   */
  readonly entityType?: SearchResultEntityType;
}

/**
 * Container for a shared player search result row
 * @param root0 - Component props
 * @param root0.player - Player data fragment
 * @param root0.effectiveSortBy - Active sort field
 * @param root0.sortFieldLabel - Sort field display label
 * @param root0.action - Action element for the right side
 * @param root0.onPress - Optional row press handler
 * @param root0.entityType - Entity type the row represents (default "Player")
 */
const PlayerSearchResultRowContainer = ({
  player,
  effectiveSortBy,
  sortFieldLabel,
  action,
  onPress,
  entityType,
}: PlayerSearchResultRowContainerProps) => {
  const playerName = useMemo(() => player?.name || "Unknown", [player]);

  const position =
    player.positions
      ?.map(p => p?.abbreviation)
      .filter(Boolean)
      .join(", ") || "—";
  const clubName = player.team?.name ?? player.teamName ?? "—";
  const metricValue = useMemo(
    () => getMetricValue(player, effectiveSortBy),
    [player, effectiveSortBy]
  );

  const handlePress = useCallback(() => {
    onPress?.();
  }, [onPress]);

  return (
    <PlayerSearchResultRowView
      playerName={playerName}
      position={position}
      clubName={clubName}
      metricLabel={SORT_FIELD_LABELS[effectiveSortBy] ?? sortFieldLabel}
      metricValue={metricValue}
      action={action}
      onPress={onPress ? handlePress : undefined}
      entityType={entityType}
    />
  );
};

export default memo(PlayerSearchResultRowContainer);
